/** @module Nothing in the interface may wait forever on a promise that never settles. */

export class TimeoutError extends Error {
  constructor(
    readonly label: string,
    readonly ms: number,
  ) {
    super(`${label} did not finish within ${ms} ms`);
    this.name = "TimeoutError";
  }
}

/**
 * Reject when a promise takes too long.
 *
 * Safari on iOS can leave indexedDB.open() pending for good; without a deadline the
 * button that started the work stays disabled and the screen looks broken.
 */
export function withTimeout<T>(work: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new TimeoutError(label, ms));
    }, ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (cause: unknown) => {
        clearTimeout(timer);
        reject(cause instanceof Error ? cause : new Error(String(cause)));
      },
    );
  });
}
